// 旧ビルダープレビュー scene/MainScene.tsx から復元・適応。
// Blockly 生成コードを new Function で実行し、common/model/animations でキャラを動かす。
// クリア条件＝全ジェム取得（原実装どおり）。
// 遷移元モード別の記録: テスト=markTested のみ / マイピース=記録なし /
// みんなのピース=playPiece（クリア・失敗とも moves / time_ms 付き）。

/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useRef, useState, useEffect, Dispatch, SetStateAction } from "react";
import * as Blockly from "blockly";
import { Scene } from "@babylonjs/core/scene";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { RootState } from "app/store";
import * as action from "common/model/animations";
import { sleep, useScale, btnDelay, usePlaySound } from "common/utils";
import { actions } from "features/builder/slice";
import { playPiece } from "features/builder/api";
import { useGameSound } from "features/courses/hook";
import { Success, Failed } from "features/courses/algorithm_tangible/game/scene/component";
import { PreviewMode } from "../types";
import { GameScene } from "./GameScene";
import { PreviewStartBtn } from "../components/PreviewStartBtn";
import { PreviewResetBtn } from "../components/PreviewResetBtn";

export const MainScene = ({
  mode,
  reload,
  setReload,
  scene,
  gameStart,
  setGameStart,
  setScene,
  event,
  workspace,
  setLoading,
  setTotalGem,
  setGemScore,
  timerStart,
  timerStop,
  timerReset,
}: {
  mode: PreviewMode;
  reload: boolean;
  setReload: (reload: boolean) => void;
  scene: Scene | null;
  gameStart: boolean;
  setGameStart: (gameStart: boolean) => void;
  setScene: (scene: Scene) => void;
  event: string;
  workspace: Blockly.WorkspaceSvg;
  setLoading: (loading: boolean) => void;
  // HUD（Score/タイム）は PreviewSceneTitle 側で表示するため状態は親が持つ
  setTotalGem: (totalGem: number) => void;
  setGemScore: Dispatch<SetStateAction<number>>;
  timerStart: () => void;
  timerStop: () => number;
  timerReset: () => void;
}) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const gameSound = useGameSound();
  const [success, setSuccess] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [successShow, setSuccessShow] = useState(false);
  const [character, _setCharacter] = useState<AbstractMesh | null>(null);
  const characterRef = useRef(character);
  const setCharacter = (c: AbstractMesh) => {
    characterRef.current = c;
    _setCharacter(c);
  };
  const sceneRef = useRef(scene);
  sceneRef.current = scene;

  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const uid = useSelector((state: RootState) => state.user.active.uid);

  // 操作ログ用: 実行した移動系コマンド数
  const movesRef = useRef(0);

  useEffect(() => {
    if (gameOver) {
      workspace.getAllBlocks(false).forEach((block: Blockly.BlockSvg) => {
        block.setMovable(true);
        block.setEditable(true);
      });
      const timeMs = timerStop();
      if (success) {
        setSuccessShow(true);
        gameSound.levelclearSound();
        gameSound.levelclearCheersSound();
        // 完成ステップの「テスト済み」は編集画面からのテストのみ
        if (mode === PreviewMode.TEST) {
          dispatch(actions.markTested());
        }
      } else {
        gameSound.failureSound();
      }
      // 操作ログはみんなのピースのプレイのみ記録（クリア・失敗とも）
      if (mode === PreviewMode.PUBLIC && pieceId) {
        playPiece({
          piece_id: pieceId,
          uid,
          cleared: success,
          moves: movesRef.current,
          time_ms: timeMs,
        }).catch((e) => console.error(e));
      }
    }
  }, [gameOver]);

  // クリア条件: ゴールに到達し、かつアイテムを全部取っていること
  // （アイテムが無いマップはゴール到達だけでクリア）
  const gemScoreRef = useRef(0);
  const totalGemRef = useRef(0);
  const reachedGoalRef = useRef(false);

  const handleSetTotalGem = (total: number) => {
    totalGemRef.current = total;
    setTotalGem(total);
  };

  const handlePlusGemScore = (total: number) => {
    gameSound.itemGetSound();
    setGemScore((prevScore) => {
      const newScore = prevScore + 1;
      gemScoreRef.current = newScore;
      // ゴールに立ったまま最後のアイテムを取った場合もクリアにする
      if (reachedGoalRef.current && newScore >= total) {
        setSuccess(true);
      }
      return newScore;
    });
  };

  const handleReachGoal = () => {
    reachedGoalRef.current = true;
    if (gemScoreRef.current >= totalGemRef.current) {
      setSuccess(true);
    }
  };

  const highlightBlock = (id: string, opt_state: boolean) => {
    if (scene && !scene.isDisposed && workspace.getBlockById(id)) {
      workspace.getBlockById(id).setHighlighted(opt_state);
    }
  };

  const util = {
    sleep,
    setSuccess,
    setGameOver,
    highlightBlock,
    action: {
      checkBtnColor: action.checkBtnColor,
      performMove: async (s: Scene, c: AbstractMesh) => {
        if (!c) {
          if (s && !s.isDisposed) s.dispose();
          setSuccess(false);
          setGameOver(true);
          return;
        }
        if (!s.isDisposed && c.isEnabled()) {
          movesRef.current += 1;
          await action.performMove(
            s,
            c,
            gameSound.moveSound,
            gameSound.fallingSound,
            false,
            { onSuccess: setSuccess, onFail: setGameOver },
            // 水のタイルに入ると落ちる（ビルダーの説明のとおり。ジャンプでとびこえる）
            { fallIntoRiver: true },
          );
        }
      },
      performJump: async (s: Scene, c: AbstractMesh) => {
        if (!c) {
          if (s && !s.isDisposed) s.dispose();
          setSuccess(false);
          setGameOver(true);
          return;
        }
        if (!s.isDisposed && c.isEnabled()) {
          movesRef.current += 1;
          await action.performJump(
            s,
            c,
            false,
            { onSuccess: setSuccess, onFail: setGameOver },
            { fallIntoRiver: true },
          );
        }
      },
      performTurnToLeft: async (s: Scene, c: AbstractMesh) => {
        if (!c) {
          if (s && !s.isDisposed) s.dispose();
          setSuccess(false);
          setGameOver(true);
          return;
        }
        if (!s.isDisposed && c.isEnabled()) {
          await action.performTurnToLeft(s, c, false);
        }
      },
      performTurnToRight: async (s: Scene, c: AbstractMesh) => {
        if (!c) {
          if (s && !s.isDisposed) s.dispose();
          setSuccess(false);
          setGameOver(true);
          return;
        }
        if (!s.isDisposed && c.isEnabled()) {
          await action.performTurnToRight(s, c, false);
        }
      },
    },
  };

  const handleStart = () => {
    btnDelay(() => {
      gameSound.startSound();
      setGameStart(true);
      movesRef.current = 0;
      timerStart();
      workspace.getAllBlocks(false).forEach((block: Blockly.BlockSvg) => {
        block.setMovable(false);
        block.setEditable(false);
      });
      try {
        const handleEvent = new Function(
          "util",
          "scene",
          "character",
          "gameSound",
          event ? event : `return () => { util.setGameOver(true); };`,
        );
        handleEvent(util, sceneRef.current, characterRef.current, gameSound)();
      } catch (error) {
        console.error(error);
        setSuccess(false);
        setGameOver(true);
      }
    });
  };

  const handleReset = () => {
    btnDelay(() => {
      if (scene && !scene.isDisposed) scene.dispose();
      setGemScore(0);
      gemScoreRef.current = 0;
      reachedGoalRef.current = false;
      setLoading(true);
      setSuccess(false);
      setGameOver(false);
      setReload(!reload);
      setGameStart(false);
      setSuccessShow(false);
      movesRef.current = 0;
      timerReset();
      workspace.getAllBlocks(false).forEach((block: Blockly.BlockSvg) => {
        block.setHighlighted(false);
        block.setMovable(true);
        block.setEditable(true);
      });
    });
  };

  const handleBack = () => {
    play();
    btnDelay(() => navigate(-1));
  };

  return (
    <>
      {gameOver && !success && <Failed scale={scale} reset={handleReset} />}

      {successShow && (
        <Success
          scale={scale}
          back={handleBack}
          reset={handleReset}
          backLabelKey={
            mode === PreviewMode.TEST
              ? "MSG_BUILDER_TEST_SUCCESS_BACK_BTN"
              : "MSG_BUILDER_PREVIEW_SUCCESS_BACK_BTN"
          }
        />
      )}

      <div className="flex-col-view items-center w-full h-full pointer-events-none">
        <div className="flex-col-center w-full h-full pointer-events-none">
          <GameScene
            reload={reload}
            config={{
              setLoading,
              setScene,
              setCharacter,
              setTotalGem: handleSetTotalGem,
              handlePlusGemScore,
              handleReachGoal,
            }}
          />
        </div>

        {/* スタート/リセットはシーン領域下端基準の absolute 配置
            （flex 押し出しはスケールで破綻するため不可） */}
        <div className="flex-col-el items-center justify-end !absolute bottom-0 w-full pointer-events-none">
          {!gameStart ? (
            <PreviewStartBtn
              scale={scale}
              disabled={
                workspace.getBlocksByType("character_event", false).length < 1 ||
                (workspace.getBlocksByType("character_event", false)[0]?.getChildren(false)
                  .length ?? 0) < 1
              }
              start={handleStart}
            />
          ) : (
            <PreviewResetBtn scale={scale} reset={handleReset} />
          )}
        </div>
      </div>
    </>
  );
};
